import React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { captureException } from "@sentry/react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod/v4-mini";

import { InclusionAgentDocSuggestion } from "@/models/InclusionAgentModel.ts";
import { Queries } from "@/queries/Queries.ts";
import { Form } from "@/uiKit/atoms/forms/Form.tsx";
import { TextToast } from "@/uiKit/atoms/Toast.tsx";
import { ControlledTextField } from "@/uiKit/molecules/ControlledTextField.tsx";
import { cz } from "@/utils/zodUtils.ts";

export type InclusionAgentPhoneFormProps = {
  suggestion: InclusionAgentDocSuggestion;
  onAction: () => void;
  onClose: () => void;
};

const formSchema = z.object({
  phone: cz.phone({ string: "form.required", refine: "form.phone.invalid" }),
});

export const INCLUSION_AGENT_PHONE_FORM_ID = (suggestion: InclusionAgentDocSuggestion) =>
  `INCLUSION_AGENT_PHONE_FORM_${suggestion.id}`;

type PhoneFormType = z.infer<typeof formSchema>;

export const InclusionAgentPhoneForm: React.FC<InclusionAgentPhoneFormProps> = ({ onClose, onAction, suggestion }) => {
  const { t } = useTranslation();
  const formContext = useForm<PhoneFormType>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      phone: suggestion.phoneNumber,
    },
  });
  const accept = Queries.practitioner.useAcceptInclusionAgentSuggestion();

  return (
    <Form
      id={INCLUSION_AGENT_PHONE_FORM_ID(suggestion)}
      onSubmit={data =>
        accept.mutate(
          { inclusionAgentId: suggestion.id, phone: data.phone },
          {
            onError: error => {
              captureException(error);
              TextToast.error(t("errors.unknownError"));
            },
            onSuccess: () => {
              TextToast.success(t("inclusionAgent.inclusionRequestSent"), t("inclusionAgent.contactPatient"));
              onClose();
              onAction();
            },
          },
        )
      }
      formContext={formContext}
    >
      <ControlledTextField
        id={`inclusion-agent-phone-${suggestion.id}`}
        name="phone"
        type="tel"
        variant="standard"
        label={t("inclusionAgent.phonePlaceholder")}
      />
    </Form>
  );
};
